import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ProposalRateGateService } from '@app/core/services/proposal-rate-gate.service';
import { UserInfoService } from '@app/core/services/user-info.service';
import { IBookProductsRequest } from '@app/models/book-products-request.model';
import { IBookingResponse } from '@app/models/booking-response.model';
import { ILoan } from '@app/models/loan.model';
import { LoanType } from '@app/models/Loan-Type';
import { ILosInterface } from '@app/models/los-interface.model';
import {
  getEmptyNadaAdvancedValueRequest,
  INadaAdvancedValueRequest
} from '@app/models/nada-vehicle-response.model';
import { IRateResponse } from '@app/models/rate-response.model';
import { IUpdateProposal } from '@app/models/update-proposal.model';
import { IUserAccMarketPlace } from '@app/models/user-info.model';
import {
  CollateralCode,
  defaultCoBorrowerDob,
  defaultCollateralType,
  defaultOtherCollateralType
} from '@app/shared/constants/globalConstants';
import { loanProducts } from '@app/shared/constants/loanProducts';
import { updateHubData } from '@app/shared/utils/hubData';
import * as _ from 'lodash';
import * as _moment from 'moment';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { Constants } from 'src/app/constants';

import { IProposal } from './../../models/proposal.model';
const moment = _moment;

const updateMmpKeys = [
  'eligible',
  'selected',
  'isSelected',
  'declined',
  'isDiscounted',
  'selectedMmpRate',
  'optionsSelected'
];
const updateGapKeys = [
  'isGapRequired',
  'selected',
  'isSelected',
  'isDiscounted',
  'selectedGapRate'
];
const updatePaymentProtectionKeys = [
  'selected',
  'isSelected',
  'selectedPaymentProtectionRate'
];
const updateVehicleReturnKeys = ['selected', 'isSelected', 'selectedVrRate'];
const updatePepKeys = ['selected', 'isSelected', 'selectedPepRate'];
const updatePpmKeys = ['selected', 'isSelected', 'selectedPpmRate'];
const updateKeyProtectionKeys = ['selected', 'isSelected', 'selectedKeyProtectionRate'];
const updateCvpKeys = ['selected', 'isSelected', 'selectedCvpRate'];
const updateSdKeys = ['selected', 'isSelected', 'selectedSdRate'];

function getPropsForProduct(productType) {
  switch (productType) {
    case 'mmp':
      return updateMmpKeys;
    case 'paymentProtection':
      return updatePaymentProtectionKeys;
    case 'gap':
      return updateGapKeys;
    case 'vehicleReturn':
      return updateVehicleReturnKeys;
    case 'pep':
      return updatePepKeys;
    case 'ppm':
      return updatePpmKeys;
    case 'keyProtection':
      return updateKeyProtectionKeys;
    case 'cvp':
      return updateCvpKeys;
    case 'sd':
      return updateSdKeys;
  }
}

@Injectable({
  providedIn: 'root'
})
export class ProposalService {
  proposal: ILoan;
  eLoanType = LoanType;
  nadaAdvancedValueRequest: INadaAdvancedValueRequest =
    getEmptyNadaAdvancedValueRequest();
  rateShortNames = {
    vehicleReturn: 'vr'
  };
  constructor(
    private http: HttpClient,
    private readonly userInfoService: UserInfoService,
    private readonly rateGate: ProposalRateGateService
  ) { }

  // retrieve proposal
  public getProposal({ id }): Observable<ILoan> {
    let headers = new HttpHeaders();
    headers = headers.append('error-whitelist', 'true');

    return this.http.get<ILoan>(
      `${Constants.appConfiguration.apiRoot}/Proposals/${id}`,
      { headers }
    );
  }

  populateBookProductsRequest(userInfo, sendEmail): IBookProductsRequest {
    return {
      portalAccountId: Number(
        userInfo[this.userInfoService.getAccountIndex(userInfo)].AccountId
      ),
      sendEmail: sendEmail,
      clientApplicationId: Constants.appConfiguration.clientApplicationId
    };
  }

  // book products, convert proposal to booking
  bookProducts(
    id: number,
    bookProductsRequest: IBookProductsRequest
  ): Observable<IBookingResponse> {
    return this.http.post<IBookingResponse>(
      `${Constants.appConfiguration.apiRoot}/Proposals/${id}/Book`,
      bookProductsRequest
    );
  }

  getRatesForProposal(proposalId): Observable<IRateResponse> {
    return this.http.get<IRateResponse>(
      `${Constants.appConfiguration.apiRoot}/Proposals/${proposalId}/Rates`
    );
  }

  /**
   * @param  {} proposal
   * @param  {} selectedRates
   * @param  {} userInfo
   * @param  {} statusInfo
   * @param  {} loanProducts
   * @returns Formats the proposal object required for update
   */
  formatDataForProposal(
    proposal: IProposal,
    selectedRates,
    userInfo: IUserAccMarketPlace,
    statusInfo: ILosInterface,
    loanProducts
  ): IUpdateProposal {
    const loanCopy = _.pickBy(_.cloneDeep(proposal), _.identity);
    _.each(loanProducts, product => {
      let selectedTxt = `selected${_.capitalize(
        this.rateShortNames[product.shortName] || product.shortName
      )}Rate`;
      if (selectedTxt === 'selectedPaymentprotectionRate') {
        selectedTxt = 'selectedPaymentProtectionRate';
      } else if (selectedTxt === 'selectedKeyprotectionRate') {
        selectedTxt = 'selectedKeyProtectionRate';
      }
      if (selectedRates[`${product.shortName}Rates`]) {
        let selectedIdKey = `${selectedTxt}Id`;
        if (selectedTxt === 'selectedPaymentProtectionRate') {
          selectedIdKey = 'selectedPpiRateId';
        } else if (selectedTxt === 'selectedKeyProtectionRate') {
          selectedIdKey = 'selectedKpRateId';
        }
        const existingRate = loanCopy[product.shortName]?.[selectedTxt];
        loanCopy[product.shortName] = _.pick(
          {
            ...loanCopy[product.shortName],
            isSelected: loanCopy[product.shortName].selected,
            [selectedTxt]: {
              rateId: selectedRates[selectedIdKey] ?? existingRate?.rateId,
              rateCollectionId: _.get(
                selectedRates[`${product.shortName}Rates`],
                ['0', 'rateCollectionId']
              ) ?? existingRate?.rateCollectionId
            }
          },
          getPropsForProduct(product.shortName)
        );
      }
      if (
        loanCopy[product.shortName] &&
        !loanCopy[product.shortName]?.[selectedTxt]?.rateId
      ) {
        loanCopy[product.shortName][selectedTxt] = null;
      }
    });
    loanCopy.id = statusInfo.quoteLoanId;
    loanCopy.losId = statusInfo.losId;
    loanCopy.portalAccountId = Number(
      userInfo[this.userInfoService.getAccountIndex(userInfo)].AccountId
    );
    loanCopy.vehicle = {
      ...loanCopy.vehicle,
      commercialUse: false,
      certifiedPreowned: false,
      secondOwner: false,
      hasLiftKitOrOversizeTires: false,
      modifiedVehicle: false,
      bodyAttributes: null,
      isSalvageTitle: loanCopy.vehicle.isSalvageTitle || false,
      purchaseDate: null,
      collateralCode: loanCopy.vehicle?.collateralCode
        ? loanCopy.vehicle.collateralCode
        : CollateralCode.GAS_VEHICLE,
      isVehicleManuallySelected: false
    };

    loanCopy.loanData = {
      ...loanCopy.loanData,
      loanType:
        proposal.vehicle.collateralType && proposal.vehicle.collateralType != 0
          ? this.eLoanType.OtherCollateral
          : this.eLoanType.Vehicle,
      coverageDate: loanCopy.loanData.coverageDate
    };

    return loanCopy;
  }

  /**
   * @param  {} proposal
   * @param  {} worksheetData
   * @param  {} userInfo
   * @param  {} statusInfo
   * @returns Formats the proposal object required for update
   */
  formatDataForWorkSheetProposal(
    proposal: IProposal,
    worksheetData,
    userInfo: IUserAccMarketPlace,
    statusInfo: ILosInterface
  ): IUpdateProposal {
    worksheetData.borrower = worksheetData.borrower || proposal.borrower;
    worksheetData.vehicle = worksheetData.vehicle || proposal.vehicle;
    const loanCopy = _.cloneDeep(worksheetData);
    _.each(loanProducts, product => {
      if (loanCopy[product.shortName]) {
        let selectedTxt = `selected${_.capitalize(
          this.rateShortNames[product.shortName] || product.shortName
        )}Rate`;
        if (selectedTxt === 'selectedPaymentprotectionRate') {
          selectedTxt = 'selectedPaymentProtectionRate';
        }
        loanCopy[product.shortName] = _.pick(
          {
            ...loanCopy[product.shortName],
            selected: loanCopy[product.shortName].selected ?? loanCopy[product.shortName].isSelected,
            isSelected: loanCopy[product.shortName].isSelected ?? loanCopy[product.shortName].selected,
            [selectedTxt]: {
              rateId: loanCopy[product.shortName].rateId,
              rateCollectionId: loanCopy[product.shortName].rateCollectionId
            }
          },
          getPropsForProduct(product.shortName)
        );

        if (!loanCopy[product.shortName][selectedTxt].rateId) {
          loanCopy[product.shortName][selectedTxt] = null;
        }
      }
    });
    loanCopy.id = statusInfo.quoteLoanId;
    loanCopy.losId = statusInfo.losId;
    loanCopy.portalAccountId = Number(
      userInfo[this.userInfoService.getAccountIndex(userInfo)].AccountId
    );
    loanCopy.vehicle = {
      ...loanCopy.vehicle,
      year: Number(loanCopy.vehicle.year),
      style: loanCopy.vehicle.style?.toString(),
      vin: loanCopy.vehicle.vin?.toString(),
      collateralType: Number(loanCopy.vehicle.collateralType),
      commercialUse: false,
      certifiedPreowned: false,
      secondOwner: false,
      hasLiftKitOrOversizeTires: false,
      modifiedVehicle: false,
      bodyAttributes: null,
      isSalvageTitle: !!loanCopy.vehicle.isSalvageTitle,
      purchaseDate: null,
      collateralCode: loanCopy.vehicle?.collateralCode
        ? loanCopy.vehicle.collateralCode
        : CollateralCode.GAS_VEHICLE,
      isVehicleManuallySelected: false,
      mockVin:
        !loanCopy.vehicle?.collateralType &&
          !proposal.vehicle?.vin &&
          !loanCopy.vehicle?.vin
          ? proposal.vehicle?.mockVin
          : '',
      validateVin: !!proposal.vehicle?.validateVin
    };
    loanCopy.loanData = {
      ...loanCopy.loanData,
      loanNumber: `${worksheetData.borrower.loanNumber}`,
      referralCode: worksheetData.borrower.referralCode,
      interestRate: worksheetData.loanData.interestRate,
      totalAmountFinanced: worksheetData.loanData.totalAmountFinanced,
      termInMonths: worksheetData.loanData.totalMonths,
      paymentsPerYear: worksheetData.loanData.paymentsPerYear,
      loanType:
        worksheetData.vehicle.collateralType &&
          worksheetData.vehicle.collateralType != 0
          ? this.eLoanType.OtherCollateral
          : this.eLoanType.Vehicle,
      coverageDate: worksheetData.mmp?.coverageDate,
      insAgent: worksheetData.borrower.insAgent,
      insAgentPhone: worksheetData.borrower.insAgentPhone,
      insName: worksheetData.borrower.insName,
      insPolicyNumber: worksheetData.borrower.insPolicyNumber
    };
    loanCopy.borrower['coBorrower'] = {
      dateOfBirth: worksheetData.borrower.coBorrower.dateOfBirth
        ? moment(worksheetData.borrower.coBorrower.dateOfBirth).format(
          'YYYY-MM-DD'
        )
        : defaultCoBorrowerDob,
      firstName: worksheetData.borrower.coBorrower.firstName,
      lastName: worksheetData.borrower.coBorrower.lastName
    };
    loanCopy.borrower.emailAddress = loanCopy.borrower.emailAddress || null;
    loanCopy.borrower.phoneNumber = loanCopy.borrower.phoneNumber || null;

    return _.pickBy(loanCopy, _.identity);
  }

  /**
   * Edits the proposal by adding/removing products
   * @param  {} id
   * @param  {} proposal
   * @param  {} proposal
   */

  saveProposal(id, proposal): Observable<string> {
    proposal.clientApplicationId =
      Constants.appConfiguration.clientApplicationId;
    return this.rateGate.guardedCall(() =>
      this.http
        .post<string>(
          `${Constants.appConfiguration.apiRoot}/Proposals/${id}`,
          proposal
        )
        .pipe(
          map((res: string) => {
            updateHubData(proposal);
            return res;
          })
        )
    );
  }

  updateProposal(loanData: ILoan): Observable<ILoan> {
    return this.rateGate.guardedCall(() =>
      this.http.post<ILoan>(
        `${Constants.appConfiguration.apiRoot}/Proposals/${loanData.loanData.id}`,
        loanData
      )
    );
  }

  // get proposal quote letter code
  public generateQuoteLetter(proposalId: number) {
    return this.http
      .get(
        `${Constants.appConfiguration.apiRootV2}/Documents/QuoteLetter/${proposalId}`,
        { responseType: 'blob' as 'json' }
      )
      .pipe(
        map((res: Blob) => {
          return new Blob([res], { type: 'application/pdf' });
        })
      );
  }

  hubData = {
    LosId: {
      parentGroup: 'losInterfaceLink',
      keyName: 'losId'
    },
    AcctNum: {
      parentGroup: 'borrower',
      keyName: 'accountNumber'
    },
    FirstName: {
      parentGroup: 'borrower',
      keyName: 'firstName'
    },
    LastName: {
      parentGroup: 'borrower',
      keyName: 'lastName'
    },
    Address1: {
      parentGroup: 'borrower',
      keyName: 'address1'
    },
    Address2: {
      parentGroup: 'borrower',
      keyName: 'address2'
    },
    City: {
      parentGroup: 'borrower',
      keyName: 'city'
    },
    State: {
      parentGroup: 'borrower',
      keyName: 'state'
    },
    Zip: {
      parentGroup: 'borrower',
      keyName: 'zipCode'
    },
    Phone: {
      parentGroup: 'borrower',
      keyName: 'phoneNumber'
    },
    EmailAddress: {
      parentGroup: 'borrower',
      keyName: 'emailAddress'
    },
    DateOfBirth: {
      parentGroup: 'borrower',
      keyName: 'dateOfBirth'
    },
    COBFirstName: {
      parentGroup: 'coBorrower',
      keyName: 'firstName'
    },
    COBLastName: {
      parentGroup: 'coBorrower',
      keyName: 'lastName'
    },
    CoBorrowerDOB: {
      parentGroup: 'coBorrower',
      keyName: 'dateOfBirth'
    },
    BorrowerDLState: {
      parentGroup: 'losInterfaceLink',
      keyName: 'dlState'
    },
    BorrowerDLNumber: {
      parentGroup: 'losInterfaceLink',
      keyName: 'dlNumber'
    },
    VIN: {
      parentGroup: 'vehicle',
      keyName: 'vin'
    },
    MockVin: {
      parentGroup: 'vehicle',
      keyName: 'mockVin'
    },
    VehicleYear: {
      parentGroup: 'vehicle',
      keyName: 'year'
    },
    VehicleMake: {
      parentGroup: 'vehicle',
      keyName: 'make'
    },
    VehicleModel: {
      parentGroup: 'vehicle',
      keyName: 'model'
    },
    VehicleStyle: {
      parentGroup: 'vehicle',
      keyName: 'style'
    },
    VehicleMakeCode: {
      parentGroup: 'vehicle',
      keyName: 'makeCode'
    },
    VehicleModelCode: {
      parentGroup: 'vehicle',
      keyName: 'modelCode'
    },
    VehicleStyleCode: {
      parentGroup: 'vehicle',
      keyName: 'styleCode'
    },
    collateralType: {
      parentGroup: 'vehicle',
      keyName: 'collateralType'
    },
    collateralCode: {
      parentGroup: 'vehicle',
      keyName: 'collateralCode'
    },
    IsVehicleNotFound: {
      parentGroup: 'vehicle',
      keyName: 'isVehicleNotFound'
    },
    Odometer: {
      parentGroup: 'vehicle',
      keyName: 'odometer'
    },
    VehiclePurchasePrice: {
      parentGroup: 'vehicle',
      keyName: 'purchasePrice'
    },
    ValidateVin: {
      parentGroup: 'vehicle',
      keyName: 'validateVin'
    },
    PurchasePrice: {
      parentGroup: 'loanData',
      keyName: 'totalAmountFinanced'
    },
    DownPayment: {
      parentGroup: 'loanData',
      keyName: 'deductions'
    },
    OtherFees: {
      parentGroup: 'loanData',
      keyName: 'otherFees'
    },
    PaymentsPerYear: {
      parentGroup: 'loanData',
      keyName: 'paymentsPerYear'
    },
    LoanDate: {
      parentGroup: 'loanData',
      keyName: 'loanDate'
    },
    LoanNum: {
      parentGroup: 'loanData',
      keyName: 'loanNumber'
    },
    FirstPaymentDate: {
      parentGroup: 'loanData',
      keyName: 'firstPaymentDate'
    },
    SecondPaymentDate: {
      parentGroup: 'loanData',
      keyName: 'secondPaymentDate'
    },
    Rate: {
      parentGroup: 'loanData',
      keyName: 'interestRate'
    },
    Term: {
      parentGroup: 'loanData',
      keyName: 'termInMonths'
    },
    InsName: {
      parentGroup: 'loanData',
      keyName: 'insName'
    },
    InsPolicyNumber: {
      parentGroup: 'loanData',
      keyName: 'insPolicyNumber'
    },
    InsAgent: {
      parentGroup: 'loanData',
      keyName: 'insAgent'
    },
    InsAgentPhone: {
      parentGroup: 'loanData',
      keyName: 'insAgentPhone'
    },
    ReferralCode: {
      parentGroup: 'loanData',
      keyName: 'referralCode'
    },
    LoanType: {
      parentGroup: 'loanData',
      keyName: 'loanType'
    },
    CombinationID: {
      parentGroup: 'losInterfaceLink',
      keyName: 'combinationId'
    },
    VIC: {
      parentGroup: 'losInterfaceLink',
      keyName: 'vic'
    },
    LoanToValue: {
      parentGroup: 'loanData',
      keyName: 'loanToValue'
    }
  };

  dateFieldsInProposal = [
    'secondPaymentDate',
    'firstPaymentDate',
    'loanDate',
    'dateOfBirth',
    'coverageDate'
  ];
  stringFieldsInProposal = [
    'zipCode',
    'phoneNumber',
    'accountNumber',
    'loanNumber',
    'address2',
    'losId',
    'insName',
    'insPolicyNumber',
    'insAgent',
    'insAgentPhone',
    'vin',
    'loanToValue'
  ];

  /**
   * To format hubdata values
   * @param hubKey {string}
   * @param val {any}
   * @returns {any}
   */
  formatHubDataValues(hubKey: string, val: any, initVal: any): any {
    if (hubKey === 'isVehicleNotFound') {
      return !!val;
    } else if (
      ['otherFees', 'deductions', 'totalAmountFinanced'].includes(hubKey)
    ) {
      return initVal && hubKey === 'totalAmountFinanced' ? val : Number(val);
    } else if (hubKey === 'interestRate') {
      return Number((+val).toFixed(6)) || 0;
    } else if (hubKey === 'termInMonths') {
      return val || 60;
    } else if (_.includes(this.stringFieldsInProposal, hubKey)) {
      return val?.toString() || '';
    } else {
      return val;
    }
  }

  /**
   * method to parse a value
   * @param val any
   * @returns string | number
   */
  parseValue(val: any): string | number {
    return !isNaN(parseFloat(val)) && !isNaN(Number(val))
      ? Number(val)
      : val || '';
  }

  /**
   * To get proposal from hub data
   * @returns {any}
   */
  public getProposalFromHubData(): any {
    const hubData = JSON.parse(sessionStorage.getItem('hubData'));
    return _.chain(hubData)
      .reduce((init, val, key: string) => {
        const hubEntry = this.hubData[key];
        if (hubEntry) {
          const parsedValue = this.parseValue(val);
          _.set(init, [hubEntry.parentGroup, hubEntry.keyName], parsedValue);
        }

        if (this.hubData[key]) {
          init[this.hubData[key].parentGroup][this.hubData[key].keyName] =
            this.formatHubDataValues(
              this.hubData[key]?.keyName,
              val,
              init[this.hubData[key].parentGroup][this.hubData[key].keyName]
            );
        }
        if (this.hubData[key]?.keyName === 'loanToValue') {
          init[this.hubData[key].parentGroup].loanToValue = Number(val);
        }

        if (
          this.hubData[key]?.keyName === 'totalAmountFinanced' &&
          !init[this.hubData[key].parentGroup].amountFinanced
        ) {
          init[this.hubData[key].parentGroup].amountFinanced = Number(val);
        }
        if (_.includes(this.dateFieldsInProposal, this.hubData[key]?.keyName)) {
          const { parentGroup, keyName } = this.hubData[key];
          if (_.includes(['LoanDate', 'FirstPaymentDate'], key) && !val) {
            val = this.setISODate(key, val, hubData);
          }
          const isDobInvalid = keyName === 'dateOfBirth' && !val;
          init[parentGroup][keyName] = isDobInvalid
            ? ''
            : moment(val)?.format('YYYY-MM-DD');
        }
        if (
          key === 'OtherCollateralVehicle' &&
          this.hubData['collateralType']
        ) {
          init[this.hubData['collateralType'].parentGroup].collateralType =
            Number(val);
          this.applyLoanType(init, val);
        }
        return init;
      }, {})
      .thru(loanData => {
        loanData.borrower['coBorrower'] = loanData.coBorrower;
        delete loanData.coBorrower;
        _.each(loanProducts, product => {
          loanData[product.shortName] = {
            selected: true,
            perPayment: '',
            daily: ''
          };
        });
        return loanData;
      })
      .value();
  }

  /**
   * Decides Loan Type
   * @param init {any}
   * @param val {any}
   */
  applyLoanType(init: any, val: any): void {
    if (Number(val) === Number(defaultOtherCollateralType)) {
      init[this.hubData['LoanType'].parentGroup].loanType =
        LoanType.OtherCollateral;
    } else if (Number(val) === Number(defaultCollateralType)) {
      init[this.hubData['LoanType'].parentGroup].loanType = LoanType.Vehicle;
    }
  }

  /**
   * To set ISO date if needed
   * @param key {string}
   * @param val {any}
   * @param hubData {any}
   * @returns {any}
   */
  setISODate(key: string, val: any, hubData: any): any {
    const dateToBeAdded = new Date();
    dateToBeAdded.setDate(dateToBeAdded.getDate() + 15);
    if (key === 'LoanDate') {
      val = !hubData.LoanDate && hubData.LosId ? val : new Date().toISOString();
    } else {
      val = dateToBeAdded.toISOString();
    }
    return val;
  }

  /**
   * Call NADA post API
   * @param  quoteLoanId {number}
   * @returns Observable {number}
   */
  setNadaAdvancedValue(quoteLoanId: number): Observable<number> {
    const params = new HttpParams().set('quoteLoanId', quoteLoanId);
    return this.http.post<number>(
      `${Constants.appConfiguration.apiRoot}/NadaAdvancedValue`,
      this.nadaAdvancedValueRequest,
      { params }
    );
  }

  /**
   * Calls NADA Get API on the worksheet page
   * @param  quoteLoanId {number}
   * @returns Observable {INadaAdvancedValueRequest}
   */
  getNadaAdvancedValue(
    quoteLoanId: number
  ): Observable<INadaAdvancedValueRequest> {
    const params = new HttpParams().set('quoteLoanId', quoteLoanId);
    return this.http.get<INadaAdvancedValueRequest>(
      `${Constants.appConfiguration.apiRoot}/NadaAdvancedValue`,
      { params }
    );
  }

  /**
   *
   * @param id
   * @param proposal
   * @returns Observable string response
   */
  public saveNewProposal(id, proposal): Observable<string> {
    proposal.losId = id;
    proposal.clientApplicationId =
      Constants.appConfiguration.clientApplicationId;
    proposal.loanData.referralCode = proposal?.borrower?.referralCode;
    return this.rateGate.guardedCall(() =>
      this.http
        .post<string>(`${Constants.appConfiguration.apiRoot}/Proposals`, proposal)
        .pipe(
          map((res: string) => {
            updateHubData(proposal);
            return res;
          })
        )
    );
  }

  public emailQuoteLetterToBorrower(proposalId: number): Observable<string> {
    return this.http.post<string>(
      `${Constants.appConfiguration.apiRootV2}/Documents/Proposal/${proposalId}/EmailToBorrower`,
      null
    );
  }

  /**
   * To get selected product Brochure
   * @param {number} portalAccountId
   * @param {number[]} docLibIds
   * @returns Observable
   */
  public getSelectedProductsBrochure(
    portalAccountId: number,
    docLibIds: number[]
  ): Observable<Blob> {
    return this.http
      .get(
        `${Constants.appConfiguration.apiRoot}/Documents/${portalAccountId}/DocumentLibraryList/${docLibIds}`,
        { responseType: 'blob' as 'json' }
      )
      .pipe(
        map((res: Blob) => {
          return new Blob([res], { type: 'application/zip' });
        })
      );
  }
}
